import { PageSize } from '@/components/DataTable/PageSizeSelector'
import { PAGE_SIZE } from '@repo/constants'
import { VisibilityState } from '@tanstack/react-table'
import { atom } from 'jotai'
import { atomFamily, atomWithStorage } from 'jotai/utils'
import { store } from '.'

const PAGE_SIZE_STORAGE_KEY = 'page-size'
const COLUMN_VISIBILITY_STORAGE_KEY = 'column-visibility'

export const pageSizeAtom = atomWithStorage<PageSize>(PAGE_SIZE_STORAGE_KEY, PAGE_SIZE, undefined, {
  getOnInit: true,
})

export const getPageSize = () => store.get(pageSizeAtom)
export const setPageSize = (pageSize: PageSize) => store.set(pageSizeAtom, pageSize)

export const DATATABLE_NAMES = {
  AGREEMENTS: 'agreements',
  BUDGET_ALLOCATIONS: 'budget-allocations',
  SALES_DEAL_ACTUALS: 'sales-deal-actuals',
  ASSORTMENTS: 'assortment',
  ASSORTMENTS_ACTIVE: 'assortment-active',
  CHANNEL_MIX: 'channel-mix',
  CHANNEL_MIX_DETAILS: 'channel-mix-details',
  COST_OF_GOODS: 'cost-of-goods',
  AZURE_IMPORTS: 'azure-imports',
  UNRELEASED_MATERIALS: 'unreleased-materials',
  PRODUCT_TYPE_VARIANTS: 'product-type-variants',
  NC_PROVINCIAL_CONTRIBUTIONS: 'nc-provincial-contributions',
  CHANNEL_CONTRIBUTIONS: 'channel-contributions',
  BPC_ACTUALS: 'bpc-actuals',
  BPC_ACTUALS_DETAILS: 'bpc-actuals-details',
  IP_ACCRUALS: 'ip-accruals',
  CALCULATION_RUN_ACCRUALS: 'calculation-run-accruals',
  PRICING_ASSUMPTIONS: 'pricing-assumptions',
  PRICING_RATES: 'pricing-rates',
  CREDIT_INDEX_SCORES: 'credit-index-scores',
  CREDIT_CUSTOMERS: 'credit-customers-v5', // Versioning resets the localStorage key for column visibility
  CREDIT_REVIEW_EVENTS: 'credit-review-events',
  CREDIT_LIMIT_REVIEWS: 'credit-limit-reviews',
  // v2: the cockpit opens on 13 of its 25 columns, with pinned edges. A stored entry REPLACES
  // `initial` wholesale rather than merging with it, and one is written the first time a reader
  // opens the Columns menu — so versioning the key is the only way a new default reaches them.
  CREDIT_BLOCKED_ORDERS: 'credit-blocked-orders-v2',
  CREDIT_BLOCKED_ORDERS_BY_DECISION: 'credit-blocked-orders-by-decision',
  MASS_CLR_RUNS: 'mass-clr-runs',
  // v2: comment/checked columns + pinned edges shipped with new default visibility (FUZE-616).
  // v3: the Recommended limit column ships visible (UAT). Versioning is the only way to change a
  // default: a stored entry REPLACES `initial` wholesale rather than merging with it, and one is
  // written the first time a user opens the Columns menu.
  MASS_CLR_LINES: 'mass-clr-lines-v3',
  MASS_CLR_SELECTION: 'mass-clr-selection',
  VERY_LOW_RISK_CUSTOMERS: 'very-low-risk-customers',
  GOOGLE_FINANCIAL_STATEMENTS: 'google-financial-statements',
  VOLUME_FORECASTS: 'volume-forecasts',
  VOLUME_SCENARIOS: 'volume-scenarios',
  VOLUME_SCENARIO_TARGET_MATERIALS: 'volume-scenario-target-materials',
  RAP_FORECASTS: 'rap-forecasts',
  MANUAL_INVESTMENT_TARGETS: 'manual-investment-targets',
  TAX_ASSUMPTIONS: 'tax-assumptions',
  AUDIT_TRAILS: 'auditTrails',
  BANNERS: 'banners',
  BRAND_VARIANTS: 'brand-variants',
  COMMERCIAL_PARTNERS: 'commercial-partners',
  BUDGET_TRACKER_BY_KEY_ACCOUNTS: 'budget-tracker-by-key-account',
  BUDGET_TRACKER: 'budget-tracker',
  INVITATIONS: 'invitations',
  LAUNCHES: 'launches',
  MATERIALS: 'materials',
  MATERIALS_LIST: 'materials-list',
  ORDERS_ALL: 'orders-all',
  ORDERS_FLAGGED: 'orders-flagged',
  ORDERS_MISSING: 'orders-missing',
  ORDERS_PAYMENT_REVIEW: 'orders-payment-review-v2',
  ORDERS_RETURNS: 'orders-returns',
  PRICE_LIST: 'price-list',
  ROLES: 'roles',
  STORES: 'stores',
  UPLOADS: 'uploads',
  EXPORTS: 'exports',
  CREDIT_EXPORTS: 'credit-exports',
  USERS: 'users',
  VARIANCE_ANALYSIS: 'variance-analysis',
}

export type DataTableName = (typeof DATATABLE_NAMES)[keyof typeof DATATABLE_NAMES]

export const columnVisibilityAtom = atomWithStorage<Record<string, VisibilityState>>(
  COLUMN_VISIBILITY_STORAGE_KEY,
  {},
  undefined,
  {
    getOnInit: true,
  },
)

export const columnVisibilityFamily = atomFamily(
  ({ name, initial = {} }: { name: DataTableName; initial?: VisibilityState }) =>
    atom(
      (get) => get(columnVisibilityAtom)[name] ?? initial,
      (get, set, updaterOrValue: VisibilityState | ((old: VisibilityState) => VisibilityState)) => {
        const prev = get(columnVisibilityAtom)
        const old = prev[name] ?? initial
        const next =
          typeof updaterOrValue === 'function'
            ? (updaterOrValue as (old: VisibilityState) => VisibilityState)(old)
            : updaterOrValue

        set(columnVisibilityAtom, { ...prev, [name]: next })
      },
    ),
  (a, b) => a.name === b.name, // equality check: same table name => same atom
)
